'use client';

import { useEffect, useState, useRef, ReactNode } from 'react';
import { redirect, RedirectType } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import { useAuthStore } from '@/context/global/stores/authStore';
import CWAddQueue from '@/context/global/components/g00/g00-d00-g014-cw-add-queue';
import CWPopup from '@/context/global/components/g00/g00-d00-g007-cw-popup';
import { useApiStore } from '@/context/global/stores/apiStore';
import { useGetOAuth } from '@/context/global/hooks/auth/useGetOAuth';
import { useQueueToast } from '@/context/global/hooks/useQueueToast';
import SongAddedToast from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/SongAddedToast';
import CWModalAdvert from '@/context/global/components/g00/g00-d00-g017-cw-modal-advert';
import { hasLockedSong } from '@/context/global/utils/hasLockedSong';
import { useFetchSongDetail } from '@/context/global/hooks/song';
import { useSettingsStore } from '@/context/global/stores/settingStore';
import { isAxiosError } from 'axios';

type ModalType = 'addQueue' | 'buyPackage' | null;

interface SongModalProviderProps {
  children: ReactNode;
}

export default function SongModalProvider({ children }: SongModalProviderProps) {
  const t = useTranslations('Package');
  const {
    currentSong,
    queue,
    setCurrentSong,
    selectedSong,
    setSelectedSong,
    selectedSongs,
    setSelectedSongs,
    pendingSong,
    setPendingSong,
    pendingSongs,
    setPendingSongs,
    addToQueue,
    playAllSongs,
    isModalNoPackageOpen,
    setIsModalNoPackageOpen,
  } = useKaraokeStore();
  const { token, user } = useAuthStore();
  const { API } = useApiStore();
  const getOAuth = useGetOAuth();
  const { toastSong, showToast } = useQueueToast();
  const { isKaraoke } = useSettingsStore();
  const { fetchSongDetail } = useFetchSongDetail(isKaraoke);

  const [modalType, setModalType] = useState<ModalType>(null);
  const [isAdvertModalOpen, setIsAdvertModalOpen] = useState(false);
  const latestBadgeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    onSelectedSong();
  }, [selectedSong, selectedSongs, token]);

  // Restore pending songs after successful login
  useEffect(() => {
    if (token && (pendingSong || pendingSongs)) {
      // User just logged in and has pending songs to play
      // เมื่อเพิ่งเข้าสู่ระบบและมีเพลง pending
      if (pendingSong) {
        setSelectedSong(pendingSong);
        setPendingSong(null);
      } else if (pendingSongs) {
        setSelectedSongs(pendingSongs);
        setPendingSongs(null);
      }
    }
  }, [token, pendingSong, pendingSongs]);

  const onSelectedSong = async () => {
    if (!selectedSong && !selectedSongs) {
      setModalType(null);
      return;
    }

    const isLoggedIn = !!token;
    if (!isLoggedIn && (!!selectedSong || !!selectedSongs)) {
      const link = await getOAuth.fetchOAuthUrl();

      if (!link) {
        throw new Error('No Link');
      }

      // บันทึกเพลงไปยังสถานะ pending
      if (selectedSong) {
        setPendingSong(selectedSong);
      }
      if (selectedSongs) {
        setPendingSongs(selectedSongs);
      }

      setSelectedSong(null);
      setSelectedSongs(null);
      redirect(link, RedirectType.push);
    }

    const userProfile = await API.User.GetUserProfile();
    const badge = userProfile.data.responseObject.userProfile.badge;
    latestBadgeRef.current = badge;

    const isEmptyObject = (obj: unknown) =>
      obj && typeof obj === 'object' && Object.keys(obj).length === 0;

    // ถ้า no package + เพลงไม่ฟรี → แสดง modal เลือกก่อนเสมอ
    const songs = selectedSongs || (selectedSong ? [selectedSong] : []);

    // เช็คว่าร้องเพลงฟรีครบ 3 เพลงหรือยัง (สำหรับ user no package - ทุกเพลง)
    if (badge === 0) {
      const songId = songs[0]?.id;
      if (songId) {
        try {
          await fetchSongDetail(songId);
        } catch (error) {
          if (isAxiosError(error) && error.response?.data?.code === 411) {
            // ถ้าไม่มีคิว → redirect ทันที
            if ((!currentSong || isEmptyObject(currentSong)) && queue.length === 0) {
              setSelectedSong(null);
              setSelectedSongs(null);
              redirect('/karaoke/continue', RedirectType.push);
              return;
            }
            // ถ้ามีคิว → fall through ไปแสดง addQueue modal ก่อน
            // handlePlay() จะเช็ค 411 อีกครั้งตอนกด "เล่นทันที"
          }
        }
      }
    }

    const hasNonFreeSong = songs.some((s) => s.conditions != null && s.conditions !== 0);
    // For NO PACKAGE users with non-free songs
    if (badge === 0 && hasNonFreeSong) {
      // If no queue exists, skip addQueue modal and go directly to package check
      if ((!currentSong || isEmptyObject(currentSong)) && queue.length === 0) {
        handlePlay();
        return;
      }
      // If queue exists, show addQueue modal
      setModalType('addQueue');
      return;
    }

    // if no song or no queue,
    // When click play all or song will redirect to karaoke
    if ((!currentSong || isEmptyObject(currentSong)) && queue.length === 0) {
      handlePlay();
      return;
    }
    setModalType('addQueue');
  };

  const handleClose = () => {
    setSelectedSong(null);
    setSelectedSongs(null);
  };

  const handlePlay = async () => {
    const songs = selectedSongs || (selectedSong ? [selectedSong] : []);

    // ถ้าเพลง lock → แสดง modal advert แทน
    if (hasLockedSong(songs, user?.badge)) {
      setModalType(null);
      setIsAdvertModalOpen(true);
      return;
    }

    // เช็ค no package
    const hasNonFreeSong = songs.some((s) => s.conditions != null && s.conditions !== 0);
    if (latestBadgeRef.current === 0 && hasNonFreeSong) {
      setModalType(null);
      setIsModalNoPackageOpen(true);
      return;
    }

    // เช็คว่าร้องเพลงฟรีครบ 3 เพลงหรือยัง (สำหรับ user no package)
    if (latestBadgeRef.current === 0) {
      const songId = songs[0]?.id;
      if (songId) {
        try {
          await fetchSongDetail(songId);
        } catch (error) {
          if (isAxiosError(error) && error.response?.data?.code === 411) {
            setModalType(null);
            setSelectedSong(null);
            setSelectedSongs(null);
            redirect('/karaoke/continue', RedirectType.push);
            return;
          }
        }
      }
    }

    if (selectedSongs) {
      // Play All: First song → currentSong, rest → queue
      playAllSongs(selectedSongs);
    } else if (selectedSong) {
      // Single song: Set as currentSong immediately
      setCurrentSong(selectedSong);
    }
    setSelectedSong(null);
    setSelectedSongs(null);
    redirect('/karaoke', RedirectType.push);
  };

  const handleAddToQueue = () => {
    const songs = selectedSongs || (selectedSong ? [selectedSong] : []);

    // ถ้าเพลง lock → แสดง modal advert แทน
    if (hasLockedSong(songs, user?.badge)) {
      setModalType(null);
      setIsAdvertModalOpen(true);
      return;
    }

    // เช็ค no package
    const hasNonFreeSong = songs.some((s) => s.conditions != null && s.conditions !== 0);
    if (latestBadgeRef.current === 0 && hasNonFreeSong) {
      setModalType(null);
      setIsModalNoPackageOpen(true);
      return;
    }

    if (selectedSongs) {
      // Play All: ไม่แสดง toast
      addToQueue(selectedSongs);
    } else if (selectedSong) {
      // Single song: แสดง toast ทันที
      addToQueue(selectedSong);
      showToast(selectedSong);
    }
    setSelectedSong(null);
    setSelectedSongs(null);
  };

  const handleCloseNoPackageModal = () => {
    setSelectedSong(null);
    setSelectedSongs(null);
    setIsModalNoPackageOpen(false);
  };
  const handleTrySinging = () => {
    setCurrentSong(selectedSong);
    setSelectedSong(null);
    setSelectedSongs(null);
    setIsModalNoPackageOpen(false);

    redirect('/karaoke', RedirectType.push);
  };
  const handleBuyPackage = () => {
    window.open(
      'https://www.ais.th/consumers/entertainment/music/karaoke#package-platinum',
      '_blank',
    );
    setIsModalNoPackageOpen(false);
    setSelectedSong(null);
    setSelectedSongs(null);
  };

  const handleCloseAdvert = () => {
    setIsAdvertModalOpen(false);
    setSelectedSong(null);
    setSelectedSongs(null);
  };

  return (
    <>
      {children}
      <CWAddQueue
        isOpen={modalType === 'addQueue'}
        onPlay={handlePlay}
        onAddToQueue={handleAddToQueue}
        onClose={handleClose}
      />

      {/* No Package Modal */}
      <CWPopup
        isOpen={isModalNoPackageOpen}
        title={t('title')}
        leftButtonText={t('sample')}
        rightButtonText={t('buy')}
        onLeftButtonClick={handleTrySinging}
        onRightButtonClick={handleBuyPackage}
        onClose={handleCloseNoPackageModal}
        leftButtonProps={{
          variant: 'primary',
          outline: true,
          className:
            'h-[60px] w-[200px] border-2 border-ais-green text-ais-green text-[24px] font-medium hover:bg-ais-green/10',
        }}
      />

      {/* Modal Advert สำหรับเพลงที่ถูก lock */}
      <CWModalAdvert
        isOpen={isAdvertModalOpen}
        onClose={handleCloseAdvert}
      />

      {/* Toast Notification */}
      <SongAddedToast songTitle={toastSong?.title || null} />
    </>
  );
}
